import { useEffect, useRef } from "react";

import {
  getUploadProgressStatus,
  useUploadStatusesByResource,
} from "components/content_library/DocumentUploadProgressProvider";
import toast from "components/ui/toast/Toast";
import { useStartBidOrchestrator } from "lib/hooks/api/bid/bids/useStartBidOrchestrator";
import { BidReportType } from "lib/types/graphQLEnums";
import { isDefined } from "lib/utils";
import { useBidOrchestratorStreamContext } from "./BidOrchestratorStreamProvider";
import { QueuedBidOrchestrator } from "./types";

/**
 * Per-bid bookkeeping we keep while deciding whether to start that bid's
 * orchestrator job. Lives in a ref (not state) because it's internal memory
 * between effect runs — changing it shouldn't trigger a re-render.
 *  - queued: the exact request this guard is tracking. If the bid gets queued
 *    again with a new request, the identity changes and we start fresh.
 *  - observedInFlight: we've seen this bid's documents actually begin uploading.
 *    Lets us tell "upload finished" apart from "there was never an upload".
 *  - dispatched: we've already kicked off the backend job — don't fire it twice.
 */
type DispatchGuard = {
  queued: QueuedBidOrchestrator;
  observedInFlight: boolean;
  dispatched: boolean;
};

/**
 * The single place in the app that starts the bid-orchestrator backend job.
 *
 * Callers never start orchestrator directly — they add a bid to a queue
 * (`queueBidOrchestrator`). This hook watches that queue and, for each bid, waits
 * until its documents have finished uploading, then fires the
 * `startBidOrchestrator` mutation. Keeping it in one hook (rather than a component
 * per bid) makes the logic easy to follow and to test.
 *
 * Mount it once, via {@link BidOrchestratorDriverHost}, inside both the orchestrator
 * and document-upload providers so it can read the queue and upload progress.
 */
export function useBidOrchestratorDriver() {
  const { queuedBidOrchestrators, entries, dequeueBidOrchestrator, trackOrchestrator } =
    useBidOrchestratorStreamContext();
  const statusesByResource = useUploadStatusesByResource();

  const guards = useRef<Record<string, DispatchGuard>>({});

  // One mutation handles every bid. Both callbacks read `input` (which carries
  // the bidId), so jobs running for different bids at the same time don't get
  // their results crossed.
  const { mutate: startOrchestrator } = useStartBidOrchestrator({
    onSuccess: (data, { input }) => {
      const { bidId, generateQualificationReport, generateRequirementSummary } = input;
      trackOrchestrator(bidId, data.startBidOrchestrator.runId, {
        qualificationReport: generateQualificationReport,
        requirementSummary: generateRequirementSummary,
      });
    },
    onError: (_, { input }) => {
      toast.error(
        `Failed to generate ${
          input.generateQualificationReport ? "Qualification" : "Requirement summary"
        } report`,
      );
      // The job never started: take the bid off the queue (so the loading page
      // sends the user back) and forget its bookkeeping so a retry can start over.
      dequeueBidOrchestrator(input.bidId);
      delete guards.current[input.bidId];
    },
  });

  useEffect(() => {
    // Forget bookkeeping for bids that have left the queue (e.g. their job
    // started). If such a bid is queued again later, it gets a clean slate.
    for (const bidId of Object.keys(guards.current)) {
      if (!queuedBidOrchestrators[bidId]) delete guards.current[bidId];
    }

    for (const [bidId, queued] of Object.entries(queuedBidOrchestrators)) {
      // Reuse this bid's guard, unless it was queued again with a new request —
      // in that case start a fresh guard so the new request actually dispatches.
      const existing = guards.current[bidId];
      const guard =
        existing && existing.queued === queued
          ? existing
          : (guards.current[bidId] = { queued, observedInFlight: false, dispatched: false });

      // A job for this report type is already running, so there's nothing to
      // start — this queue entry is just what's keeping the loading page open.
      // Drop it.
      const entry = entries[bidId];
      const covered =
        queued.reportType === BidReportType.Qualification
          ? entry?.generates.qualificationReport
          : entry?.generates.requirementSummary;
      if (entry && covered) {
        dequeueBidOrchestrator(bidId);
        continue;
      }

      const statuses = statusesByResource[bidId] ?? [];
      const { allUploaded } = getUploadProgressStatus(statuses);

      // Documents are still uploading to S3 — wait. Remember we saw them in
      // progress so we can later tell a real "upload finished" from a bid that
      // simply never had an upload.
      if (statuses.length > 0 && !allUploaded) {
        guard.observedInFlight = true;
        continue;
      }
      // The caller told us to expect an upload, so don't start the job until
      // we've actually seen one begin — otherwise we'd run against a stale or
      // empty document list.
      if (queued.expectsUpload && !guard.observedInFlight) continue;
      if (guard.dispatched) continue;
      guard.dispatched = true;

      // Hand the backend the bid's existing documents plus anything just
      // uploaded (deduped).
      const newDocIds = statuses.map((s) => s.documentId).filter(isDefined);
      startOrchestrator({
        input: {
          bidId,
          documentIds: [...new Set([...queued.existingDocumentIds, ...newDocIds])],
          extractBidDetails: queued.extractBidDetails,
          generateQualificationReport: queued.reportType === BidReportType.Qualification,
          generateRequirementSummary: queued.reportType === BidReportType.RequirementSummary,
        },
      });
    }
  }, [
    queuedBidOrchestrators,
    entries,
    statusesByResource,
    dequeueBidOrchestrator,
    startOrchestrator,
  ]);
}
